import React from 'react';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchDelete, fetchGet, fetchPost } from '../../store/fetch';
import { ComponentSetStatePromised } from '../utils/ComponentSetStatePromised';
import { showSavedToast } from '../utils/toastMessages';
import { ValidatingFormContext } from '../validatingElements';
import { emptyJsonSchemaValidationErrors } from './emptyJsonSchemaValidationErrors';
import { JsonSchemaValidationErrors } from './JsonSchemaValidationErrors';
import { validateJsonSchemaFormItem } from './validateJsonSchemaFormItem';

function updateValidationErrorsForOne(schema: any, object: any, errors: JsonSchemaValidationErrors, name: string) {
    const { errors: newErrors, hasErrors } = validateJsonSchemaFormItem(schema, object);
    if (newErrors.has(name)) {
        return { errors: errors.set(name, newErrors.get(name)), hasErrors };
    }
    return { errors: errors.remove(name), hasErrors };
}

interface IValidatingFormProps {
    // eslint-disable-next-line no-shadow
    onChange?: (key: any, value: any, item: any, onChange: (key: any, value: any) => Promise<void>) => Promise<void>;
    onIsItemValidChange?: undefined | ((isItemValid: boolean) => void | Promise<void>);
    createSchema: any;
    updateSchema: any;
    createUrl?: undefined | string;
    createUrlQueryParameters?: undefined | { [key: string]: string | undefined };
    itemUrl?: undefined | string;
    itemUrlQueryParameters?: undefined | { [key: string]: string | undefined };
    updateUrl?: undefined | string;
    updateUrlQueryParameters?: undefined | { [key: string]: string | undefined };
    onSubmitted?: undefined | ((id: string) => void | Promise<void>);
    id?: undefined | string;
    defaultItem?: undefined | any;
    subForm?: undefined | boolean;
    overrideSubmit?: undefined | ((data: { id?: undefined | string; item: any }) => Promise<{ id: string }>);
    showSavedToast?: undefined | boolean;
    children: React.ReactNode;
}

interface IValidatingFormState {
    values: { [key: string]: string };
    errors: JsonSchemaValidationErrors;
    hasErrors: boolean;
    isSaving: boolean;
    item?: any;
    assetsToRemove: { assetId: string; surveyId: string }[];
    isBusy: boolean;
}

export function ValidatingJsonSchemaForm(props: IValidatingFormProps) {
    const translateKey = useTranslate();

    return <ValidatingJsonSchemaFormComponent {...props} t={translateKey} />;
}
class ValidatingJsonSchemaFormComponent extends ComponentSetStatePromised<
    IValidatingFormProps & { t: (key: string) => string },
    IValidatingFormState
> {
    constructor(props: IValidatingFormProps & { t: (key: string) => string }) {
        super(props);

        this.state = {
            values: {},
            errors: emptyJsonSchemaValidationErrors,
            hasErrors: false,
            isSaving: false,
            assetsToRemove: [],
            isBusy: false,
        };
    }

    public override async componentDidMount() {
        await this.init();
    }
    public override async componentDidUpdate(prevProps: IValidatingFormProps) {
        if (prevProps.id !== this.props.id) {
            await this.init();
        }
    }

    public override render() {
        return (
            <>
                {' '}
                {this.state.item && (
                    <ValidatingFormContext.Provider
                        value={{
                            onChange: async (name, value) => this.handleChange(name, value),
                            onDoValidate: async () => this.doValidate(),
                            onDoValidateOne: async (name) => this.doValidateOne(name),
                            addAssetToRemove: (assetId: string, surveyId: string) =>
                                this.setState({ assetsToRemove: [...this.state.assetsToRemove, { assetId, surveyId }] }),
                            item: this.state.item,
                            errors: this.state.errors,
                            submit: async () => this.submit(),
                            isDisabledForLoading: this.state.isSaving || this.state.isBusy,
                            setBusy: (isBusy: boolean) => this.setState({ isBusy }),
                            getItem: (key) => this.getPropertyWithDot(key, this.state.item),
                        }}
                    >
                        <form
                            id="admin-form"
                            className={this.props.subForm ? 'bg-light dark p10' : ''}
                            onSubmit={(e) => {
                                e.preventDefault();
                                void this.submit();
                            }}
                        >
                            {this.props.children}
                        </form>
                    </ValidatingFormContext.Provider>
                )}
            </>
        );
    }

    private async init() {
        if (this.props.id) {
            await this.loadItem();
        } else {
            await this.setStatePromised({ item: this.props.defaultItem || {} });
        }

        const schema = this.props.id ? this.props.updateSchema : this.props.createSchema;
        const { hasErrors } = validateJsonSchemaFormItem(schema, this.state.item);
        await this.setStatePromised({ hasErrors });
    }

    private async handleChange(name: string, value: any) {
        await this.setStatePromised(({ item }) => ({ item: { ...item, [name]: value } }));
        if (name.includes('.')) {
            const firstKey = name.split('.')[0]!;
            const secondKey = name.split('.')[1]!;
            const { item } = this.state;
            if (!item[firstKey]) {
                item[firstKey] = {};
            }
            item[firstKey] = { ...item[firstKey], [secondKey]: value };
            await this.setStatePromised({ item });
        }
        if (this.props.onChange) {
            await this.props.onChange(
                name,
                value,
                this.state.item,
                async (changedKey: any, changedValue: any) => await this.handleChange(changedKey, changedValue),
            );
        }
    }

    private getPropertyWithDot(key: string, item: any) {
        if (!item) {
            return undefined;
        }
        if (key.includes('.')) {
            const firstKey = key.split('.')[0]!;
            const secondKey = key.split('.')[1]!;
            return item[firstKey][secondKey];
        }
        return item[key];
    }

    private async doValidate() {
        const schema = this.props.id ? this.props.updateSchema : this.props.createSchema;
        const { errors, hasErrors } = validateJsonSchemaFormItem(schema, this.state.item);
        if (this.props.onIsItemValidChange) {
            await this.props.onIsItemValidChange(!hasErrors);
        }
        await this.setStatePromised({ errors, hasErrors });
    }

    private async doValidateOne(name: string) {
        const schema = this.props.id ? this.props.updateSchema : this.props.createSchema;
        const { errors, hasErrors } = updateValidationErrorsForOne(schema, this.state.item, this.state.errors, name);
        if (this.props.onIsItemValidChange) {
            await this.props.onIsItemValidChange(!hasErrors);
        }
        await this.setStatePromised({ errors, hasErrors });
    }

    private async submit() {
        const schema = this.props.id ? this.props.updateSchema : this.props.createSchema;
        const { hasErrors, item, errors } = validateJsonSchemaFormItem(schema, this.state.item);
        if (hasErrors) {
            await this.setStatePromised({ errors });
            return;
        }
        await this.setStatePromised({ hasErrors: false, errors: emptyJsonSchemaValidationErrors });
        if (this.state.isBusy || this.state.isSaving) {
            return;
        }

        await this.setStatePromised({ isSaving: true });
        let id: string;
        if (this.props.overrideSubmit) {
            // eslint-disable-next-line prefer-destructuring
            id = (await this.props.overrideSubmit({ id: this.props.id, item })).id;
        } else {
            if (this.props.id) {
                if (this.props.updateUrl) {
                    await fetchPost(this.props.updateUrl, this.props.updateUrlQueryParameters, item);
                } else {
                    if (!this.props.itemUrl) {
                        throw new Error('itemUrl cannot be empty');
                    }
                    await fetchPost(this.props.itemUrl, this.props.itemUrlQueryParameters, item);
                }
                // eslint-disable-next-line prefer-destructuring
                id = this.props.id;
            } else {
                if (!this.props.createUrl) {
                    throw new Error('createUrl cannot be empty');
                }
                const result = await fetchPost(this.props.createUrl, this.props.createUrlQueryParameters, item);
                // eslint-disable-next-line prefer-destructuring
                id = result.json.id;
            }
        }
        for (const assetToRemove of this.state.assetsToRemove) {
            await fetchDelete(`/api/surveys/${assetToRemove.surveyId}/assets/${assetToRemove.assetId}`);
        }
        await this.setStatePromised({ isSaving: false, assetsToRemove: [] });
        if (this.props.onSubmitted !== undefined) {
            await this.props.onSubmitted(id);
        }
        if (this.props.showSavedToast) {
            void showSavedToast(this.props.t);
        }
    }

    private async loadItem() {
        if (!this.props.itemUrl) {
            throw new Error('itemUrl cannot be empty');
        }
        const result = await fetchGet(this.props.itemUrl, this.props.itemUrlQueryParameters);
        const item = result.json;

        for (const rootKey of Object.keys(item)) {
            if (item[rootKey] && typeof item[rootKey] === 'object' && !Array.isArray(item[rootKey])) {
                for (const objectKey of Object.keys(item[rootKey])) {
                    item[`${rootKey}.${objectKey}`] = item[rootKey][objectKey];
                }
            }
        }
        await this.setStatePromised({ item });
    }
}
